import { z } from 'zod';
import { endpoint } from './endpoint.js';
import { ReservationSourceSchema, ReservationStatusSchema } from '../schemas/reservation-status.js';
import {
  ReservationListQuerySchema,
  ReservationListResponseSchema,
  UpdateReservationDatesSchema,
  OverrideReservationSchema,
} from '../schemas/reservation-list.js';
import {
  BatchReserveRequestSchema,
  BatchReserveResponseSchema,
  BatchReleaseRequestSchema,
  BatchReleaseResponseSchema,
  BatchReleasePreviewRequestSchema,
  BatchReleasePreviewResponseSchema,
  BatchReservePreviewRequestSchema,
  BatchReservePreviewResponseSchema,
} from '../schemas/reservation-batch.js';

const ReservationIdSchema = z.coerce.number().int().positive();

export const ReservationRecordSchema = z.object({
  id: ReservationIdSchema,
  mediaUnitId: z.number().int().positive(),
  offerId: z.number().int().positive().nullable(),
  holderUserId: z.number().int().positive(),
  status: ReservationStatusSchema,
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
  targetVisibilityDays: z.number().int().positive().nullable(),
  ttlExpiresAt: z.coerce.date().nullable(),
  /**
   * The contract-signing deadline — set to 7 calendar days after a promotion
   * to HARD_RESERVATION, null for every other status. Distinct from
   * ttlExpiresAt. See CU-86cb4wky2.
   */
  signingDeadlineAt: z.coerce.date().nullable(),
  source: ReservationSourceSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ReservationRecord = z.infer<typeof ReservationRecordSchema>;

const CreateReservationSchema = z.object({
  mediaUnitId: z.number().int().positive(),
  offerId: z.number().int().positive().nullable().optional(),
  status: ReservationStatusSchema,
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
  targetVisibilityDays: z.number().int().positive().max(365).nullable().optional(),
  /**
   * Optional manual TTL expiry (CU-86cak92z8), honored only for
   * SOFT_RESERVATION/HARD_RESERVATION — mirrors `OverrideReservationSchema`'s
   * existing manual-TTL semantics. Silently ignored for OPTION/SOLD (which
   * always carry a null TTL). Rejected server-side if it resolves to a
   * past/stale instant — see `ReservationsService.validateCreateInput`.
   */
  ttlExpiresAt: z.coerce.date().optional(),
});

export type CreateReservationBody = z.infer<typeof CreateReservationSchema>;

const UpgradeReservationSchema = z.object({
  id: ReservationIdSchema,
  expectedStatus: z.literal('SOFT_RESERVATION'),
  /**
   * CU-86can6mmu: when true, allows acting on another user's reservation.
   * Requires the caller to hold `reservation.force_release` — same
   * admin-override capability that already gates force-cancel and
   * force-displacement on date edits.
   */
  force: z.boolean().optional(),
});

const CloseSaleSchema = z.object({
  id: ReservationIdSchema,
  expectedStatus: z.enum(['SOFT_RESERVATION', 'HARD_RESERVATION']),
  /** CU-86can6mmu: same force-on-others'-reservation semantics as UpgradeReservationSchema. */
  force: z.boolean().optional(),
});

/**
 * Date-only YYYY-MM-DD calendar date. Deliberately a plain, validated string
 * rather than `z.coerce.date()` — coercing "YYYY-MM-DD" produces a
 * UTC-midnight instant, which loses the Warsaw date-only semantics the server
 * needs to combine with the reservation's preserved local time of day.
 */
const DateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a YYYY-MM-DD date')
  .refine((value) => {
    const parts = value.split('-').map(Number);
    const year = parts[0] ?? 0;
    const month = parts[1] ?? 0;
    const day = parts[2] ?? 0;
    const roundTrip = new Date(Date.UTC(year, month - 1, day));
    return (
      roundTrip.getUTCFullYear() === year &&
      roundTrip.getUTCMonth() === month - 1 &&
      roundTrip.getUTCDate() === day
    );
  }, 'Invalid calendar date');

/**
 * Manual effective-expiry deadline edit (manual-reservation-expiry-editing,
 * CU-86cb7qgc9). Guarded by `reservation.extend`. Distinct from
 * `OverrideReservationSchema`/`UpdateReservationDatesSchema`: authorization,
 * validation, and persisted fields differ from status override and
 * date-range editing.
 */
const EditReservationExpiryDeadlineSchema = z.object({
  id: ReservationIdSchema,
  expiryDate: DateOnlySchema,
  reason: z.string().trim().min(1, 'Reason is required'),
  expectedStatus: z.enum(['SOFT_RESERVATION', 'HARD_RESERVATION']),
});

export const reservations = {
  create: endpoint('POST', '/reservations', CreateReservationSchema, ReservationRecordSchema),
  cancel: endpoint(
    'DELETE',
    '/reservations/:id',
    z.object({ id: ReservationIdSchema, force: z.coerce.boolean().optional() }),
    z.void(),
  ),
  upgradeHard: endpoint(
    'PATCH',
    '/reservations/:id/upgrade-hard',
    UpgradeReservationSchema,
    ReservationRecordSchema,
  ),
  closeSale: endpoint(
    'PATCH',
    '/reservations/:id/close-sale',
    CloseSaleSchema,
    ReservationRecordSchema,
  ),
  editExpiryDeadline: endpoint(
    'PATCH',
    '/reservations/:id/expiry-deadline',
    EditReservationExpiryDeadlineSchema,
    ReservationRecordSchema,
  ),
  list: endpoint('GET', '/reservations', ReservationListQuerySchema, ReservationListResponseSchema),
  updateDates: endpoint(
    'PATCH',
    '/reservations/:id/dates',
    UpdateReservationDatesSchema,
    ReservationRecordSchema,
  ),
  override: endpoint(
    'PATCH',
    '/reservations/:id/override',
    OverrideReservationSchema,
    ReservationRecordSchema,
  ),
  batchReserve: endpoint(
    'POST',
    '/reservations/batch-reserve',
    BatchReserveRequestSchema,
    BatchReserveResponseSchema,
  ),
  batchReservePreview: endpoint(
    'POST',
    '/reservations/batch-reserve/preview',
    BatchReservePreviewRequestSchema,
    BatchReservePreviewResponseSchema,
  ),
  batchRelease: endpoint(
    'POST',
    '/reservations/batch-release',
    BatchReleaseRequestSchema,
    BatchReleaseResponseSchema,
  ),
  // Read-only preview (reservation-engine / "Batch release preview") — no
  // reservation or queue row is written. Declared as a literal path segment
  // under batch-release, same convention as batchReservePreview above.
  batchReleasePreview: endpoint(
    'POST',
    '/reservations/batch-release/preview',
    BatchReleasePreviewRequestSchema,
    BatchReleasePreviewResponseSchema,
  ),
};
